import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, CardContent, toast } from '@mlm/ui';
import {
  getMerchantMe,
  listMerchantReviews,
  reviewPhotoUrl,
  setMerchantReviewFeatured,
  type MerchantReview,
} from '../lib/api';

/**
 * Featured Feedback manager. The merchant pins reviews of their OWN products to
 * a "Featured Feedback" section on their storefront shop page. No cap — feature
 * as many as desired. Ownership is enforced server-side. Mirrors the gate + list
 * patterns of the other merchant pages.
 */
export function FeedbackPage() {
  const qc = useQueryClient();
  const { data: me } = useQuery({ queryKey: ['merchant-me'], queryFn: getMerchantMe });
  const approved = me?.merchant.status === 'approved';
  const [featuredOnly, setFeaturedOnly] = useState(false);

  const { data: reviews = [], isLoading } = useQuery({
    queryKey: ['merchant-reviews', featuredOnly],
    queryFn: () => listMerchantReviews({ featuredOnly: featuredOnly || undefined }),
    enabled: approved,
  });

  const toggle = useMutation({
    mutationFn: (r: MerchantReview) => setMerchantReviewFeatured(r.id, !r.featured),
    onSuccess: (_d, r) => {
      toast.success(r.featured ? 'Removed from Featured Feedback.' : 'Added to Featured Feedback.');
      qc.invalidateQueries({ queryKey: ['merchant-reviews'] });
    },
  });

  const featuredCount = reviews.filter((r) => r.featured).length;

  if (!approved) {
    return (
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold">Featured Feedback</h1>
        <p className="text-sm text-muted-foreground">Available once your shop is approved.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Featured Feedback</h1>
          <p className="text-sm text-muted-foreground">
            Pin the reviews you want shown in the &ldquo;Featured Feedback&rdquo; section of your
            shop. No limit — feature as many as you like.
          </p>
        </div>
        <label className="flex shrink-0 items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={featuredOnly}
            onChange={(e) => setFeaturedOnly(e.target.checked)}
          />
          Featured only{!featuredOnly && featuredCount > 0 ? ` (${featuredCount})` : ''}
        </label>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : reviews.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {featuredOnly ? 'No featured reviews yet.' : 'No reviews on your products yet.'}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {reviews.map((r) => (
            <Card key={r.id}>
              <CardContent className="flex items-start gap-4 py-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Stars rating={r.rating} />
                    {r.featured && <Badge>Featured</Badge>}
                    <span className="ml-auto text-xs text-muted-foreground">
                      {new Date(r.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {r.reviewerName} · <span className="opacity-70">{r.productName}</span>
                  </div>
                  {r.comment && <p className="mt-1.5 text-sm">{r.comment}</p>}
                  {r.hasPhoto && (
                    <div className="mt-2 flex gap-1.5">
                      {Array.from({ length: r.photoCount }).map((_, i) => (
                        <img
                          key={i}
                          src={reviewPhotoUrl(r.productId, r.id, i)}
                          alt=""
                          className="h-14 w-14 rounded object-cover"
                        />
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  variant={r.featured ? 'outline' : 'default'}
                  size="sm"
                  disabled={toggle.isPending}
                  onClick={() => toggle.mutate(r)}
                >
                  {r.featured ? 'Unfeature' : 'Feature'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function Stars({ rating }: { rating: number }) {
  return (
    <span
      className="text-sm tracking-tight text-amber-400"
      aria-label={`${rating} star${rating === 1 ? '' : 's'}`}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={n <= rating ? '' : 'text-muted-foreground/30'}>
          ★
        </span>
      ))}
    </span>
  );
}
